import type { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { Integration } from "../../integrations";

/**
 * These are variables that are not part of the schema, but are used in email and SMS templates
 * They are used to reference data from the customer event
 * WARNING: Do not overlap naming with schema variables
 */
export enum NonSchemaReferenceVariableName {
  MOST_RECENTLY_VIEWED_PRODUCT_LINK = "mostRecentlyViewedProductLink",
  MOST_RECENTLY_VIEWED_PRODUCT_NAME = "mostRecentlyViewedProductName",
  REP_NAME = "repName",
  BRAND_NAME = "brandName",
  SUPPORT_EMAIL = "supportEmail",
  CUSTOMER_LOYALTY_LION_POINTS = "customerLoyaltyLionPoints",
  SMILE_POINTS_BALANCE = "smilePointsBalance",
  SMILE_POINTS_TO_EARN = "smilePointsToEarn",
  PRODUCT_LINK = "productLink",
  STORE_LINK = "storeLink",
}

export function isNonSchemaReferenceVariableName(
  variableName: string,
): variableName is NonSchemaReferenceVariableName {
  return Object.values(NonSchemaReferenceVariableName).includes(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    variableName as NonSchemaReferenceVariableName,
  );
}

export const NonSchemaReferenceVariable: Record<
  NonSchemaReferenceVariableName,
  SchemaField
> = {
  [NonSchemaReferenceVariableName.MOST_RECENTLY_VIEWED_PRODUCT_LINK]: {
    key: NonSchemaReferenceVariableName.MOST_RECENTLY_VIEWED_PRODUCT_LINK,
    dataType: "Url",
    documentation: "The link to the most recently viewed product.",
  },
  [NonSchemaReferenceVariableName.MOST_RECENTLY_VIEWED_PRODUCT_NAME]: {
    key: NonSchemaReferenceVariableName.MOST_RECENTLY_VIEWED_PRODUCT_NAME,
    dataType: "Text",
    documentation: "The name of the most recently viewed product.",
  },
  [NonSchemaReferenceVariableName.REP_NAME]: {
    key: NonSchemaReferenceVariableName.REP_NAME,
    dataType: "Text",
    documentation: "The name of the representative (for RECOVER messages).",
  },
  [NonSchemaReferenceVariableName.BRAND_NAME]: {
    key: NonSchemaReferenceVariableName.BRAND_NAME,
    dataType: "Text",
    documentation: "The brand/store name.",
  },
  [NonSchemaReferenceVariableName.SUPPORT_EMAIL]: {
    key: NonSchemaReferenceVariableName.SUPPORT_EMAIL,
    dataType: "Text",
    documentation: "The support email address for customer inquiries.",
  },
  [NonSchemaReferenceVariableName.CUSTOMER_LOYALTY_LION_POINTS]: {
    key: NonSchemaReferenceVariableName.CUSTOMER_LOYALTY_LION_POINTS,
    dataType: "Maybe Number",
    documentation: "The loyalty points of the customer.",
    shouldInclude: (integrations) =>
      integrations[Integration.LOYALTY_LION] === true,
  },
  [NonSchemaReferenceVariableName.SMILE_POINTS_BALANCE]: {
    key: NonSchemaReferenceVariableName.SMILE_POINTS_BALANCE,
    dataType: "Maybe Number",
    documentation: "The customer's current Smile.io loyalty points balance",
    shouldInclude: (integrations) => integrations[Integration.SMILE] === true,
  },
  [NonSchemaReferenceVariableName.SMILE_POINTS_TO_EARN]: {
    key: NonSchemaReferenceVariableName.SMILE_POINTS_TO_EARN,
    dataType: "Maybe Number",
    documentation:
      "Estimated Smile.io points the customer would earn by completing this purchase",
    shouldInclude: (integrations) => integrations[Integration.SMILE] === true,
  },
  // Resolved at send time from the flow's `cartContext` and scoped
  // to product specific triggers like low-inventory or price-drop
  [NonSchemaReferenceVariableName.PRODUCT_LINK]: {
    key: NonSchemaReferenceVariableName.PRODUCT_LINK,
    dataType: "Url",
    documentation:
      "Link to the product this flow is about (e.g. the low-stock or price-dropped product).",
  },
  [NonSchemaReferenceVariableName.STORE_LINK]: {
    key: NonSchemaReferenceVariableName.STORE_LINK,
    dataType: "Url",
    documentation: "Link to the merchant's storefront homepage.",
  },
};

/**
 * Stand-ins for previews, where the values that need a Shopify or loyalty call
 * cannot be fetched. Kept beside the definitions so a new variable is obvious
 * when it has no example.
 */
export const exampleNonSchemaVariableValues: Record<
  NonSchemaReferenceVariableName,
  string
> = {
  [NonSchemaReferenceVariableName.MOST_RECENTLY_VIEWED_PRODUCT_LINK]:
    "https://example.com/products/product",
  [NonSchemaReferenceVariableName.MOST_RECENTLY_VIEWED_PRODUCT_NAME]: "Product",
  [NonSchemaReferenceVariableName.PRODUCT_LINK]:
    "https://example.com/products/product",
  [NonSchemaReferenceVariableName.CUSTOMER_LOYALTY_LION_POINTS]: "100",
  [NonSchemaReferenceVariableName.SMILE_POINTS_BALANCE]: "100",
  [NonSchemaReferenceVariableName.SMILE_POINTS_TO_EARN]: "10",
  [NonSchemaReferenceVariableName.STORE_LINK]: "https://example.com",
  [NonSchemaReferenceVariableName.REP_NAME]: "Rep",
  [NonSchemaReferenceVariableName.BRAND_NAME]: "Store",
  [NonSchemaReferenceVariableName.SUPPORT_EMAIL]: "support@example.com",
};
